import {useCallback, useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  AGENT_SKILLS_QUERY,
  AgentSkillsQueryData,
  archiveSkill as archiveSkillApi,
  createSkill,
  SkillSummary,
} from 'llm/chat-library/skills/SkillsDAO';
import EntityStore from 'stores/EntityStore';
import * as ImportUtil from 'util/ImportUtil';

interface UseSkillsResult {
  skills: Array<SkillSummary>;
  isLoading: boolean;
  archiveSkill: (skillId: string, reason: string) => Promise<void>;
  createNewSkill: (file: File) => Promise<void>;
  refresh: () => Promise<void>;
}

export default function useSkills({enabled = true}: {enabled?: boolean} = {}): UseSkillsResult {
  const queryResult = useSkippableBenchlingQuery<AgentSkillsQueryData>(AGENT_SKILLS_QUERY, {
    skip: !enabled,
    fetchPolicy: 'network-only',
  });

  const currentUserId = EntityStore.currentUser()?.id ?? null;

  const skills: Array<SkillSummary> = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return queryResult.data.agentSkills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      description: skill.description,
      createdAt: skill.createdAt,
      isOwnedByCurrentUser: currentUserId != null && skill.creatorId === currentUserId,
      creatorName: skill.creatorName ?? undefined,
    }));
  }, [queryResult, currentUserId]);

  const isLoading = !enabled ? false : queryResult.type === QueryResultType.PENDING;

  const archiveSkillHandler = useCallback(
    async (skillId: string, reason: string) => {
      await archiveSkillApi(skillId, reason);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    },
    [queryResult]
  );

  const createNewSkill = useCallback(
    async (file: File) => {
      const s3Location = await Promise.resolve(ImportUtil.upload(file));
      await createSkill(s3Location!);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    },
    [queryResult]
  );

  const refresh = useCallback(async () => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      await queryResult.refetch();
    }
  }, [queryResult]);

  return {
    skills,
    isLoading,
    archiveSkill: archiveSkillHandler,
    createNewSkill,
    refresh,
  };
}
